import { inject, Injectable } from '@angular/core';
import { CentralConfigService } from '@core/services/central-config.service';
import { UserConfigManagementService } from '@core/services/user-config-management.service';
import { EntityName } from '@shared/enums/entity-name.enum';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { GlobalFilterCriteria } from '@shared/interfaces/filters/global-filter.interface';
import { StringUtils } from '@shared/utils/string-utils';
import { TimeUtils } from '@shared/utils/time-utils';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GlobalFilterStoredService {
  private readonly filterPaneSource$ = new BehaviorSubject<
    Record<string, unknown>
  >({});
  private readonly filterTokenAddedFromOutside$ = new BehaviorSubject<number>(
    null
  );
  private readonly currentFilterToken$ = new BehaviorSubject<number>(null);
  private readonly topicFilter$ = new BehaviorSubject({});
  filterReady$ = new BehaviorSubject<boolean>(false);

  centralConfig = inject(CentralConfigService);

  constructor(private readonly userConfig: UserConfigManagementService) {
    this.registerFilterTokenPropagation();
  }

  /**
   * Deactivates the cached innovation space. Used when a page is opened
   * without the organizationId param: in-app navigation always carries the
   * param (see registerFilterTokenPropagation), so a param-less page load
   * means the link came from outside the app.
   */
  async resetInnovationSpace(): Promise<void> {
    const currentOrgIds: number[] = await this.userConfig.get(
      StorageEnum.currentFilterToken
    );

    if (!currentOrgIds?.length) return;

    await this.userConfig.set(StorageEnum.currentFilterToken, []);

    const globalFilters: GlobalFilterCriteria = await this.userConfig.get(
      StorageEnum.globalFilters
    );

    if (!globalFilters) return;

    globalFilters.organizationIds = [];
    globalFilters.organizationId = '';

    const expiredDate = TimeUtils.getDateWithOffset(new Date(), 1);

    await this.userConfig.set(StorageEnum.globalFilters, globalFilters, {
      expires: expiredDate,
      path: '/',
    });
  }

  async getFilterCriteria(): Promise<GlobalFilterCriteria> {
    const currentFiltertokenJson = await this.userConfig.get(
      StorageEnum.currentFilterToken
    );
    const activeFilterChannel = await this.userConfig.get(
      StorageEnum.activeFilterChannel
    );

    let currentOrgIds: number[] = [];
    let threadId = null,
      threadEntity = '';

    if (currentFiltertokenJson) {
      currentOrgIds = currentFiltertokenJson;
      if (currentFiltertokenJson.length > 0 && activeFilterChannel) {
        threadId = activeFilterChannel.id;
        threadEntity =
          activeFilterChannel.entityName === EntityName.Event
            ? EntityName.Event
            : '';
      }
    }

    const defaultCriteria: GlobalFilterCriteria = {
      keyword: '',
      organizationIds: currentOrgIds,
      ...(threadId ? { threadId } : {}),
      threadEntity,
    };
    const globalFilters = await this.userConfig.get(StorageEnum.globalFilters);

    if (globalFilters) {
      const result: GlobalFilterCriteria = globalFilters;
      if (result) {
        result.organizationIds = currentOrgIds;
        if (threadId) {
          result.threadId = threadId;
        }
        result.threadEntity = threadEntity;
        return result;
      }
    }

    return defaultCriteria;
  }

  async saveFilterCriteria(
    searchKeyword: string,
    path: string,
    organizationId: number,
    threadId: number,
    threadEntity: string
  ): Promise<GlobalFilterCriteria> {
    const filterCriteria: GlobalFilterCriteria = {
      keyword: searchKeyword || '',
      filteringPath: path,
      organizationIds: [],
      organizationId: '',
      ...(threadId && { threadId, threadEntity }),
    };

    if (organizationId) {
      filterCriteria.organizationIds.push(organizationId);
      filterCriteria.organizationId = organizationId.toString();
    }

    const expiredDate = TimeUtils.getDateWithOffset(new Date(), 1);

    await this.userConfig.set(
      StorageEnum.currentFilterToken,
      filterCriteria.organizationIds
    );

    if (this.centralConfig.innovationSpaceId) {
      StringUtils.removeParamFromUrl(UrlParam.FilterTokenId);
    }

    await this.userConfig.set(StorageEnum.globalFilters, filterCriteria, {
      expires: expiredDate,
      path: '/',
    });

    return filterCriteria;
  }

  appendFilterTokenOnUrl(currentOrgIds: number[]): void {
    if (currentOrgIds.length > 0) {
      StringUtils.setQueryParamOnUrl(
        UrlParam.FilterTokenId,
        currentOrgIds[0].toString(),
        null,
        true
      );
    } else {
      StringUtils.removeParamFromUrl(UrlParam.FilterTokenId, null, true);
    }
  }

  //#region HANDLE FILTER PANE FLOW
  public getFilterPaneSource(): Observable<Record<string, unknown>> {
    return this.filterPaneSource$.asObservable();
  }

  public updateFilterPaneSource(filterDto: Record<string, unknown>): void {
    this.filterPaneSource$.next(filterDto);
  }
  //#endregion End Handle Filter Pane Flow

  //#region HANDLE FILTER TOKEN FROM OUTSIDE GLOBAL FILTER
  public getFilterTokenAddedFromOutside(): Observable<unknown> {
    return this.filterTokenAddedFromOutside$.asObservable();
  }

  public setFilterTokenFromOutSide(organizationId: number): void {
    this.filterTokenAddedFromOutside$.next(organizationId);
  }
  //#endregion End Handle Filter Token From Outside Global Filter

  //#region HANDLE CURRENT FILTER TOKEN -> Using to replace EventBus, step by step
  public getCurrentFilterToken(): Observable<number> {
    return this.currentFilterToken$.asObservable();
  }

  public setCurrentFilterToken(organizationId: number): void {
    this.currentFilterToken$.next(organizationId);
  }

  getCurrentFilterTokenSnapshot(): number | null {
    return this.currentFilterToken$.getValue();
  }
  //#endregion End Handle Current Filter Token

  public getTopicFilter(): Observable<object> {
    return this.topicFilter$.asObservable();
  }

  public setTopicFilter(threadId: number, threadEntity: string): void {
    this.topicFilter$.next({
      threadId,
      threadEntity,
    });
  }

  /**
   * Rewrites same-origin links at click time so they carry the active
   * innovation space param of the current url. This makes the param the
   * single source of truth for the active space: a page load without it can
   * only come from outside the app (pasted link, email, ...). Registered on
   * pointerdown (covers middle click before its default action) and click
   * (covers keyboard activation); the rewrite is idempotent.
   */
  private registerFilterTokenPropagation(): void {
    const propagate = (event: Event) =>
      this.propagateFilterTokenToAnchor(event);

    document.addEventListener('pointerdown', propagate, true);
    document.addEventListener('click', propagate, true);
  }

  private propagateFilterTokenToAnchor(event: Event): void {
    if (!(event.target instanceof Element)) return;

    const anchor = event.target.closest<HTMLAnchorElement>('a[href]');

    if (!anchor || anchor.hasAttribute('download')) return;

    const rawHref = anchor.getAttribute('href');

    // Hash-only links never reload the page, adding a param would force one
    if (!rawHref || rawHref.startsWith('#')) return;

    const rewrittenHref = StringUtils.propagateParamFromCurrentUrl(
      anchor.href,
      UrlParam.FilterTokenId
    );

    if (rewrittenHref !== anchor.href) {
      anchor.href = rewrittenHref;
    }
  }
}
